import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "store/useAuthStore";
import { hasRouteAccess } from "utils/routePermissions";
import { useEffect, useState } from "react";
import EmptyState from "components/empty-states/EmptyState";
import { useAuthStore } from "store/useAuthStore";

const RoleBasedRoute = ({
  children,
  allowedRoles = [],
  redirectTo = "/dashboard",
  showError = true,
}) => {
  const user = useAuthStore((store) => store.user);
  const { loading, isAuthenticated } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      // Give some time for auth state to settle
      await new Promise(resolve => setTimeout(resolve, 50));
      
      if (!isAuthenticated || !user) {
        // console.log("User not authenticated, redirecting to login");
        navigate("/auth/login", { 
          replace: true,
          state: { from: location } // Preserve intended destination
        });
      } else {
        setIsChecking(false);
      }
    };

    checkAuth();
  }, [isAuthenticated, user, navigate, location]);

  // Show loading state
  if (loading || isChecking) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // If not authenticated after checking, don't render children
  if (!isAuthenticated || !user) {
    return null;
  }

  const userRole = user.role;

  // Check if user role is in allowed roles
  const hasAccess =
    allowedRoles.length === 0 || allowedRoles.includes(userRole);

  // Also check route permissions if allowedRoles not specified
  const routeAccess =
    allowedRoles.length === 0
      ? hasRouteAccess(location.pathname, userRole)
      : true;

  const authorized = hasAccess && routeAccess;
  // console.log("Route access:", authorized, hasAccess, routeAccess, userRole);

  if (!authorized) {
    if (showError) {
      return (
        <EmptyState
          type="unauthorized"
          title="Access Denied"
          message={`You don't have permission to access this page. Required roles: ${allowedRoles.join(
            ", "
          )}`}
        />
      );
    }

    // Redirect to user's dashboard
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
};

export default RoleBasedRoute;